import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { InstructorService } from '@cougargrades/services'
import { API_ORIGIN } from '@cougargrades/services/environment'
import { computeUDI, deriveUDIInputs } from '@cougargrades/utils'
import { useLiteSearchResults } from '../lib/services/useSearchResults'
import { Skeleton } from '../components/ui/Skeleton'
import { GradeBar } from '../components/ui/GradeBar'
import { UDIBadge } from '../components/ui/UDIBadge'
import type { SearchResult } from '@cougargrades/models/dto'

const searchParams = z.object({
  q: z.string().optional().default(''),
})

export const Route = createFileRoute('/search')({
  validateSearch: searchParams,
  head: () => ({
    meta: [
      { title: 'Search | Unilytics' },
      { name: 'description', content: 'Search courses and professors on Unilytics' },
    ],
  }),
  component: RouteComponent,
})

type ResultTab = 'courses' | 'instructors'

// ─── Types ────────────────────────────────────────────────────────────────────

type GradeCounts = { A: number; B: number; C: number; D: number; F: number; W: number }

import type { UDIResult } from '@cougargrades/utils'

interface CourseSummary {
  avgGPA: number
  difficultyScore: number
  udiResult: UDIResult
  gradeCounts: GradeCounts
  sectionCount: number
  instructorCount: number
  firstTaught: string
  lastTaught: string
}

interface InstructorSummary {
  avgGPA: number
  difficultyScore: number
  udiResult: UDIResult
  gradeCounts: GradeCounts
  sectionCount: number
  courseCount: number
  department: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Minimal fields we need from the full /api/course/:name response
interface CourseApiShape {
  badges: Array<{ key: string; text: string; color: string }>
  dataGrid: { rows: Array<{ A: number; B: number; C: number; D: number; F: number; W: number }> }
  sectionCount: number
  instructorCount: number
  firstTaught: string
  lastTaught: string
}

const KNOWN_DEPT_CODES = new Set([
  'MATH', 'COSC', 'ENGL', 'BIOL', 'CHEM', 'PHYS', 'HIST', 'POLS',
  'ACCT', 'ART', 'ARCH', 'BUSI', 'COMM', 'ECON', 'EDUC', 'ENGR',
  'GEOL', 'LING', 'MUSI', 'NURS', 'PHIL', 'PSYC', 'SOCI', 'SPAN',
])

/** Returns true when the query looks like a department code search rather than a professor name search. */
function looksLikeDeptCode(q: string): boolean {
  if (!q) return false
  const trimmed = q.trim()
  // Short queries (≤4 chars) or all-uppercase letter strings are almost certainly dept codes
  return (
    trimmed.length <= 4 ||
    KNOWN_DEPT_CODES.has(trimmed.toUpperCase()) ||
    /^[A-Z]{2,6}$/.test(trimmed)
  )
}

function parseCourseTitle(title: string): { code: string; name: string } {
  const colonIdx = title.indexOf(': ')
  if (colonIdx >= 0) {
    return { code: title.slice(0, colonIdx).trim(), name: title.slice(colonIdx + 2).trim() }
  }
  return { code: title, name: '' }
}

function gpaColor(gpa: number): string {
  if (gpa >= 3.0) return '#059669'
  if (gpa >= 2.0) return '#EAB308'
  return '#DC2626'
}

function enrollmentToGrades(enrollment: Array<{ key: string | number; title: string; value: number }>): GradeCounts {
  const pick = (key: string) =>
    enrollment.find(e => String(e.key).toUpperCase() === key || e.title.toUpperCase() === key)?.value ?? 0
  return { A: pick('A'), B: pick('B'), C: pick('C'), D: pick('D'), F: pick('F'), W: pick('W') }
}

// ─── Route component ──────────────────────────────────────────────────────────

function RouteComponent() {
  const { q } = Route.useSearch()
  const [activeTab, setActiveTab] = useState<ResultTab>('courses')

  const { data: results, status } = useLiteSearchResults(q, q.length > 0)
  const isPending = status === 'pending'
  const DID_LOAD = status === 'success'

  const courseResults = (results ?? []).filter(r => r.type === 'course')
  // Filter out false positives: keep only instructors where the query matches their last name.
  // Instructor hrefs are "/i/lastname, firstname" so lastName is before the comma.
  const instructorResults = (results ?? [])
    .filter(r => r.type === 'instructor')
    .filter(r => {
      if (!q) return true
      const lastNameLower = r.href.slice(3).split(',')[0].trim()
      return lastNameLower.includes(q.toLowerCase())
    })
  const totalResults = courseResults.length + instructorResults.length
  const hasQuery = q.length > 0
  const noResults = DID_LOAD && hasQuery && totalResults === 0

  // Extract ids for enrichment — slicing off "/c/" and "/i/" prefixes
  const courseIds = useMemo(
    () => courseResults.map(r => decodeURIComponent(r.href.slice(3))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [courseResults.map(r => r.key).join(',')]
  )
  const instructorNames = useMemo(
    () => instructorResults.map(r => decodeURIComponent(r.href.slice(3))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [instructorResults.map(r => r.key).join(',')]
  )

  const { data: enrichment, isLoading: enrichmentLoading } = useQuery({
    queryKey: ['search-enrichment', courseIds, instructorNames],
    queryFn: async () => {
      const [courseSummaries, instructorSummaries] = await Promise.all([
        Promise.all(
          courseIds.map(async (id, idx): Promise<CourseSummary | null> => {
            const url = `${API_ORIGIN.origin}/api/course/${encodeURIComponent(id)}`
            if (idx === 0) {
              console.log('[search:debug] first course id:', JSON.stringify(id), '| url:', url)
            }
            try {
              const resp = await fetch(url)
              if (idx === 0) {
                console.log('[search:debug] first course response status:', resp.status)
              }
              if (!resp.ok) return null
              const json = await resp.json() as CourseApiShape
              const avgGPA = parseFloat(json.badges?.find(b => b.key === 'gpa')?.text ?? '0') || 0
              const gradeCounts: GradeCounts = { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 }
              for (const row of (json.dataGrid?.rows ?? [])) {
                gradeCounts.A += row.A ?? 0
                gradeCounts.B += row.B ?? 0
                gradeCounts.C += row.C ?? 0
                gradeCounts.D += row.D ?? 0
                gradeCounts.F += row.F ?? 0
                gradeCounts.W += row.W ?? 0
              }
              const udiResult = computeUDI(deriveUDIInputs({ avgGPA, grades: gradeCounts, sections: json.sectionCount ?? 0, schoolId: 'uh' }))
              if (idx === 0) {
                console.log('[search:debug] first course avgGPA:', avgGPA, '| udi:', udiResult.udi, '| gradeCounts:', gradeCounts)
              }
              return {
                avgGPA,
                difficultyScore: udiResult.udi,
                udiResult,
                gradeCounts,
                sectionCount: json.sectionCount ?? 0,
                instructorCount: json.instructorCount ?? 0,
                firstTaught: json.firstTaught ?? '',
                lastTaught: json.lastTaught ?? '',
              }
            } catch (err) {
              if (idx === 0) console.error('[search:debug] first course fetch error:', err)
              return null
            }
          })
        ),
        Promise.all(
          instructorNames.map(async (name): Promise<InstructorSummary | null> => {
            try {
              const svc = new InstructorService()
              const data = await svc.GetInstructor(name)
              if (!data) return null
              const avgGPA = parseFloat(data.badges.find(b => b.key === 'gpa')?.text ?? '0') || 0
              const gradeCounts = enrollmentToGrades(data.enrollment)
              const udiResult = computeUDI(deriveUDIInputs({ avgGPA, grades: gradeCounts, sections: data.sectionCount, schoolId: 'uh' }))
              return {
                avgGPA,
                difficultyScore: udiResult.udi,
                udiResult,
                gradeCounts,
                sectionCount: data.sectionCount,
                courseCount: data.courseCount,
                department: data.meta.descriptionDepartmentsInvolved,
              }
            } catch {
              return null
            }
          })
        ),
      ])

      const courses: Record<string, CourseSummary | null> = {}
      courseIds.forEach((id, i) => { courses[id] = courseSummaries[i] })

      const instructors: Record<string, InstructorSummary | null> = {}
      instructorNames.forEach((name, i) => { instructors[name] = instructorSummaries[i] })

      return { courses, instructors }
    },
    enabled: DID_LOAD && (courseIds.length > 0 || instructorNames.length > 0),
    staleTime: 1000 * 60 * 5,
  })

  const showSkeleton = isPending || (DID_LOAD && !noResults && !enrichment && enrichmentLoading)
  const isDeptCodeQuery = looksLikeDeptCode(q)

  return (
    <div style={{ fontFamily: 'var(--font-family-sans)', background: '#F8FAFC', minHeight: '100vh' }}>

      {/* ── SUMMARY BAR ── */}
      {hasQuery && !noResults && (
        <div style={{ background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', padding: '10px 0' }}>
          <div className="max-w-6xl mx-auto px-4" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>
              {isPending ? (
                <Skeleton width={200} height={14} />
              ) : (
                <>
                  <strong style={{ color: '#0D1B2A' }}>{courseResults.length}</strong>{' '}
                  {courseResults.length === 1 ? 'course' : 'courses'}{' · '}
                  <strong style={{ color: '#0D1B2A' }}>{instructorResults.length}</strong>{' '}
                  {instructorResults.length === 1 ? 'instructor' : 'instructors'}{' for '}
                  <span style={{ color: '#475569', fontStyle: 'italic' }}>"{q}"</span>
                </>
              )}
            </p>
            <Link
              to="/search"
              search={{ q: '' }}
              style={{
                fontSize: 12, fontWeight: 600, color: '#94A3B8',
                textDecoration: 'none', whiteSpace: 'nowrap',
                padding: '4px 10px', borderRadius: 6,
                border: '1px solid #E2E8F0', background: '#fff',
                transition: 'color 0.15s',
              }}
            >
              Clear ×
            </Link>
          </div>
        </div>
      )}

      {/* ── TABS BAR ── */}
      {hasQuery && !noResults && (
        <div style={{ background: '#fff', borderBottom: '1px solid #E2E8F0' }}>
          <div className="max-w-6xl mx-auto px-4" style={{ display: 'flex' }}>
            <SearchTabButton
              active={activeTab === 'courses'}
              onClick={() => setActiveTab('courses')}
              count={isPending ? null : courseResults.length}
            >
              Courses
            </SearchTabButton>
            <SearchTabButton
              active={activeTab === 'instructors'}
              onClick={() => setActiveTab('instructors')}
              count={isPending ? null : isDeptCodeQuery ? 0 : instructorResults.length}
            >
              Instructors
            </SearchTabButton>
          </div>
        </div>
      )}

      {/* ── BODY ── */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {noResults ? (
          <FullEmptyState query={q} />
        ) : !hasQuery ? (
          <StartSearchState />
        ) : (
          <div>
              {showSkeleton ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {Array.from({ length: activeTab === 'courses' ? 5 : 3 }).map((_, i) => (
                    <SearchSkeletonCard key={i} isCourse={activeTab === 'courses'} />
                  ))}
                </div>
              ) : activeTab === 'courses' ? (
                courseResults.length === 0
                  ? <TabEmptyState query={q} type="courses" />
                  : <>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {courseResults.map(r => (
                          <CourseCard
                            key={r.key}
                            result={r}
                            summary={enrichment?.courses[decodeURIComponent(r.href.slice(3))] ?? null}
                          />
                        ))}
                      </div>
                      {isDeptCodeQuery && (
                        <p style={{ marginTop: 14, fontSize: 12, color: '#94A3B8', textAlign: 'center' }}>
                          Looking for a professor?{' '}
                          <span style={{ color: '#64748B' }}>Search by last name instead</span>{' '}
                          <span style={{ opacity: 0.6 }}>(e.g. try <em>Smith</em> or <em>Johnson</em>)</span>
                        </p>
                      )}
                    </>
              ) : isDeptCodeQuery ? (
                <DeptCodeInstructorHint query={q} />
              ) : (
                instructorResults.length === 0
                  ? <TabEmptyState query={q} type="instructors" />
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {instructorResults.map(r => (
                        <InstructorCard
                          key={r.key}
                          result={r}
                          summary={enrichment?.instructors[decodeURIComponent(r.href.slice(3))] ?? null}
                        />
                      ))}
                    </div>
              )}

          </div>
        )}
      </div>
    </div>
  )
}

// ─── Tab button ───────────────────────────────────────────────────────────────

function SearchTabButton({
  active, onClick, children, count,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  count: number | null
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent', border: 'none',
        borderBottom: active ? '3px solid var(--school-primary)' : '3px solid transparent',
        padding: '10px 20px 9px',
        fontSize: 14, fontWeight: active ? 700 : 500,
        color: active ? '#0D1B2A' : '#64748B',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 8,
        transition: 'color 0.15s, border-color 0.15s',
        fontFamily: 'var(--font-family-sans)',
        letterSpacing: '-0.01em',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#0D1B2A' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#64748B' }}
    >
      {children}
      {count !== null && (
        <span style={{
          background: active ? 'var(--school-primary)' : '#F1F5F9',
          color: active ? '#0A1628' : '#64748B',
          fontSize: 11, fontWeight: 700,
          padding: '2px 8px', borderRadius: 20,
          minWidth: 24, textAlign: 'center',
          transition: 'all 0.15s',
        }}>
          {count}
        </span>
      )}
    </button>
  )
}

// ─── Course card ──────────────────────────────────────────────────────────────

function CourseCard({ result, summary }: { result: SearchResult; summary: CourseSummary | null }) {
  const { code, name } = parseCourseTitle(result.title)
  const gpa = summary?.avgGPA ?? null
  const hasGradeData = summary !== null && (summary.gradeCounts.A + summary.gradeCounts.B + summary.gradeCounts.C + summary.gradeCounts.D + summary.gradeCounts.F + summary.gradeCounts.W) > 0

  const metaParts: string[] = []
  if (summary?.sectionCount) metaParts.push(`${summary.sectionCount.toLocaleString()} sections`)
  if (summary?.instructorCount) metaParts.push(`${summary.instructorCount.toLocaleString()} instructors`)
  if (summary?.firstTaught && summary?.lastTaught) metaParts.push(`${summary.firstTaught} – ${summary.lastTaught}`)

  return (
    <a
      href={result.href}
      style={{
        display: 'block', background: '#fff', borderRadius: 14,
        border: '1px solid rgba(13,27,42,0.07)',
        borderLeft: '4px solid var(--school-primary)',
        boxShadow: '0 2px 8px rgba(13,27,42,0.05)',
        padding: '16px 20px', textDecoration: 'none', cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,27,42,0.12)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,27,42,0.05)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        {/* Left: code + name */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: 'inline-block',
            fontFamily: 'var(--font-family-mono)', fontSize: 12, fontWeight: 700,
            color: 'var(--school-primary)', background: 'rgba(var(--school-primary-rgb),0.08)',
            padding: '3px 10px', borderRadius: 6,
            border: '1px solid rgba(var(--school-primary-rgb),0.2)',
            letterSpacing: '0.02em', marginBottom: 6,
          }}>
            {code}
          </span>
          {name && (
            <div style={{
              fontSize: 15, fontWeight: 700, color: '#0D1B2A',
              lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {name}
            </div>
          )}
        </div>

        {/* Right: GPA + difficulty + arrow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {summary?.udiResult !== undefined && (
            <UDIBadge udi={summary.udiResult.udi} />
          )}
          <div style={{ textAlign: 'center', minWidth: 44 }}>
            <div style={{
              fontFamily: 'var(--font-family-mono)', fontSize: 22, fontWeight: 700,
              color: gpa !== null && gpa > 0 ? gpaColor(gpa) : '#E2E8F0',
              lineHeight: 1, letterSpacing: '-0.03em',
            }}>
              {gpa !== null && gpa > 0 ? gpa.toFixed(2) : '—'}
            </div>
            <div style={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Avg GPA
            </div>
          </div>
          <span style={{ fontSize: 18, color: 'var(--school-primary)', fontWeight: 700, lineHeight: 1 }}>→</span>
        </div>
      </div>

      {/* Grade bar */}
      {hasGradeData && (
        <div style={{ marginTop: 12 }}>
          <GradeBar grades={summary!.gradeCounts} height={8} />
        </div>
      )}

      {/* Metadata row */}
      {metaParts.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#94A3B8', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {metaParts.map((part, i) => (
            <span key={i}>{part}</span>
          ))}
        </div>
      )}
    </a>
  )
}

// ─── Instructor card ──────────────────────────────────────────────────────────

function InstructorCard({ result, summary }: { result: SearchResult; summary: InstructorSummary | null }) {
  const gpa = summary?.avgGPA ?? null
  const hasGradeData = summary !== null && (summary.gradeCounts.A + summary.gradeCounts.B + summary.gradeCounts.C + summary.gradeCounts.D + summary.gradeCounts.F + summary.gradeCounts.W) > 0

  const metaParts: string[] = []
  if (summary?.sectionCount) metaParts.push(`${summary.sectionCount.toLocaleString()} sections`)
  if (summary?.courseCount) metaParts.push(`${summary.courseCount.toLocaleString()} courses`)

  return (
    <a
      href={result.href}
      style={{
        display: 'block', background: '#fff', borderRadius: 14,
        border: '1px solid rgba(13,27,42,0.07)',
        borderLeft: '4px solid #0D1B2A',
        boxShadow: '0 2px 8px rgba(13,27,42,0.05)',
        padding: '16px 20px', textDecoration: 'none', cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,27,42,0.12)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,27,42,0.05)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        {/* Left: name + department */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 15, fontWeight: 700, color: '#0D1B2A',
            lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            marginBottom: summary?.department ? 4 : 0,
          }}>
            {result.title}
          </div>
          {summary?.department && (
            <div style={{ fontSize: 12, color: 'var(--school-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {summary.department}
            </div>
          )}
        </div>

        {/* Right: GPA + difficulty + arrow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {summary?.udiResult !== undefined && (
            <UDIBadge udi={summary.udiResult.udi} />
          )}
          <div style={{ textAlign: 'center', minWidth: 44 }}>
            <div style={{
              fontFamily: 'var(--font-family-mono)', fontSize: 22, fontWeight: 700,
              color: gpa !== null && gpa > 0 ? gpaColor(gpa) : '#E2E8F0',
              lineHeight: 1, letterSpacing: '-0.03em',
            }}>
              {gpa !== null && gpa > 0 ? gpa.toFixed(2) : '—'}
            </div>
            <div style={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Avg GPA
            </div>
          </div>
          <span style={{ fontSize: 18, color: '#0D1B2A', fontWeight: 700, lineHeight: 1 }}>→</span>
        </div>
      </div>

      {/* Grade bar */}
      {hasGradeData && (
        <div style={{ marginTop: 12 }}>
          <GradeBar grades={summary!.gradeCounts} height={8} />
        </div>
      )}

      {/* Metadata row */}
      {metaParts.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#94A3B8', display: 'flex', gap: 12 }}>
          {metaParts.map((part, i) => (
            <span key={i}>{part}</span>
          ))}
        </div>
      )}
    </a>
  )
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SearchSkeletonCard({ isCourse }: { isCourse: boolean }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14,
      border: '1px solid rgba(13,27,42,0.06)',
      borderLeft: `4px solid ${isCourse ? 'rgba(var(--school-primary-rgb),0.25)' : 'rgba(13,27,42,0.12)'}`,
      padding: '16px 20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <Skeleton width={isCourse ? 100 : 160} height={20} style={{ marginBottom: 8, borderRadius: 6 }} />
          <Skeleton width="60%" height={15} style={{ borderRadius: 4 }} />
        </div>
        <Skeleton width={52} height={42} style={{ borderRadius: 8, flexShrink: 0 }} />
      </div>
      <Skeleton width="100%" height={8} style={{ borderRadius: 4 }} />
    </div>
  )
}

// ─── Empty states ─────────────────────────────────────────────────────────────

function StartSearchState() {
  const suggestions = ['COSC', 'MATH', 'ENGL', 'PHYS']
  return (
    <div style={{ padding: '64px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>🔍</div>
      <p style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>
        Search for a course or instructor
      </p>
      <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 24, lineHeight: 1.6 }}>
        Try a department code, a course number, or a professor's last name
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {suggestions.map(s => (
          <Link
            key={s}
            to="/search"
            search={{ q: s }}
            style={{
              padding: '6px 16px', borderRadius: 20,
              border: '1.5px solid rgba(var(--school-primary-rgb),0.3)',
              color: 'var(--school-primary)', fontSize: 12, fontWeight: 600,
              background: 'rgba(var(--school-primary-rgb),0.05)', textDecoration: 'none',
            }}
          >
            {s}
          </Link>
        ))}
      </div>
    </div>
  )
}

function DeptCodeInstructorHint({ query }: { query: string }) {
  return (
    <div style={{ padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 36, marginBottom: 14, opacity: 0.2 }}>👩‍🏫</div>
      <p style={{ fontSize: 15, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>
        Search by professor last name to find instructors
      </p>
      <p style={{ fontSize: 13, color: '#94A3B8', maxWidth: 360, margin: '0 auto 20px', lineHeight: 1.6 }}>
        <span style={{ fontStyle: 'italic' }}>"{query}"</span> looks like a department code — try searching a last name
        instead to find professors.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {['Smith', 'Johnson', 'Williams', 'Brown'].map(name => (
          <Link
            key={name}
            to="/search"
            search={{ q: name }}
            style={{
              padding: '6px 14px', borderRadius: 20,
              border: '1.5px solid rgba(13,27,42,0.15)',
              color: '#475569', fontSize: 12, fontWeight: 600,
              background: '#fff', textDecoration: 'none',
            }}
          >
            Try <em>{name}</em>
          </Link>
        ))}
      </div>
    </div>
  )
}

function TabEmptyState({ query, type }: { query: string; type: ResultTab }) {
  return (
    <div style={{ padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 14, opacity: 0.2 }}>🔍</div>
      <p style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>
        No {type === 'courses' ? 'courses' : 'instructors'} found for{' '}
        <span style={{ color: 'var(--school-primary)' }}>'{query}'</span>
      </p>
      <p style={{ fontSize: 13, color: '#94A3B8', maxWidth: 340, margin: '0 auto', lineHeight: 1.6 }}>
        {type === 'courses'
          ? 'Try searching by department code (e.g. "COSC") or a partial course name'
          : 'Try searching by last name only (e.g. "Smith")'}
      </p>
    </div>
  )
}

function FullEmptyState({ query }: { query: string }) {
  const suggestions = ['COSC', 'MATH', 'ENGL']
  return (
    <div style={{ padding: '80px 24px', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: 64, marginBottom: 20, opacity: 0.18 }}>🔍</div>
      <p style={{ fontSize: 22, fontWeight: 800, color: '#0D1B2A', marginBottom: 10, letterSpacing: '-0.02em' }}>
        No results for <span style={{ color: 'var(--school-primary)' }}>'{query}'</span>
      </p>
      <p style={{ fontSize: 14, color: '#94A3B8', marginBottom: 28, lineHeight: 1.7 }}>
        Double-check the spelling or try a broader search like a department code.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 28 }}>
        {suggestions.map(s => (
          <Link
            key={s}
            to="/search"
            search={{ q: s }}
            style={{
              padding: '8px 18px', borderRadius: 20,
              border: '1.5px solid rgba(var(--school-primary-rgb),0.35)',
              color: 'var(--school-primary)', fontSize: 13, fontWeight: 600,
              background: 'rgba(var(--school-primary-rgb),0.06)', textDecoration: 'none',
            }}
          >
            Try '{s}'
          </Link>
        ))}
      </div>
      <Link
        to="/"
        style={{ fontSize: 13, color: '#94A3B8', textDecoration: 'none', borderBottom: '1px solid rgba(148,163,184,0.3)' }}
      >
        ← Back to home
      </Link>
    </div>
  )
}
